import type { Timestamp } from "firebase-admin/firestore";
import { z } from "zod";

const zTimestamp = z.custom<Timestamp>((val) => val && typeof val.toDate === "function", {
    message: "Expected Firestore Timestamp",
});

type DateFieldsToTimestamps<T extends z.ZodRawShape> = {
    [K in keyof T]: T[K] extends z.ZodDate
        ? z.ZodType<Timestamp>
        : T[K] extends z.ZodOptional<z.ZodDate>
          ? z.ZodOptional<z.ZodType<Timestamp>>
          : T[K];
};

function isNullableDate(schema: z.ZodTypeAny): boolean {
    if (schema instanceof z.ZodNullable) {
        return schema.unwrap() instanceof z.ZodDate;
    }
    return false;
}

function convertDateField(value: z.ZodTypeAny): z.ZodTypeAny {
    if (value instanceof z.ZodDefault) {
        const inner = convertDateField(value.removeDefault());
        const defaultValue = value._def.defaultValue();
        return inner.default(defaultValue);
    }
    if (value instanceof z.ZodOptional) {
        return convertDateField(value.unwrap()).optional();
    }
    if (isNullableDate(value)) {
        return zTimestamp.nullable();
    }
    if (value instanceof z.ZodDate) {
        return zTimestamp;
    }
    return value;
}

function toFirestoreSchema<T extends z.ZodRawShape>(schema: z.ZodObject<T>): z.ZodObject<DateFieldsToTimestamps<T>> {
    const newShape = Object.fromEntries(
        Object.entries(schema.shape).map(([key, value]) => [key, convertDateField(value as z.ZodTypeAny)])
    );

    return z.object(newShape) as z.ZodObject<DateFieldsToTimestamps<T>>;
}

export default toFirestoreSchema;
